import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardBody, Container } from "reactstrap";
import { Divider, FormControlLabel, Grid, Switch } from "@mui/material";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import LoadingDialog from "../../../components/Common/LoadingDialog";
import Notification from "../../../components/Common/Notification";
import CustomButton from "../../../components/Form/CustomButton";
import CustomTextField from "../../../components/Form/CustomTextField";
import FormEditors from "../../Forms/FormEditors";
import {
    createContentPage,
    getContentPage,
    updateContentPage,
} from "../../../store/setting/settingApi";
import "../setting.css";

const defaultContentPageForm = () => ({
    title: "",
    slug: "",
    meta_description: "",
    meta_keywords: "",
    body_html: "",
    status: 1,
    show_in_footer: 0,
    is_faq: 0,
});

const isToggleOn = (value) => Number(value) === 1;

const EditContentPage = ({ type, userPermission }) => {
    const navigate = useNavigate();
    const { ID } = useParams();
    const isEdit = type === "Edit";
    const editorRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [editable, setEditable] = useState(false);
    const [notification, setNotification] = useState({ open: false, msg: "", type: "success" });
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(defaultContentPageForm);

    useEffect(() => {
        if (userPermission?.length > 0) {
            userPermission.includes("edit-setting") && setEditable(true);
            !userPermission.includes("view-setting") && navigate("/not-authorized");
        }
    }, [userPermission, navigate]);

    useEffect(() => {
        if (!isEdit) {
            setForm(defaultContentPageForm());
            setErrors({});
            return;
        }

        if (ID) {
            loadPage();
        }
    }, [ID, isEdit]);

    const loadPage = async () => {
        setLoading(true);
        const response = await getContentPage(ID);
        if (response?.code === "success" && response?.data) {
            const page = response.data;
            setForm({
                title: page.title || "",
                slug: page.slug || "",
                meta_description: page.meta_description || "",
                meta_keywords: page.meta_keywords || "",
                body_html: page.body_html || "",
                status: isToggleOn(page.status) ? 1 : 0,
                show_in_footer: isToggleOn(page.show_in_footer) ? 1 : 0,
                is_faq: isToggleOn(page.is_faq) ? 1 : 0,
            });
        }
        setLoading(false);
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: null }));
    };

    const handleSave = async () => {
        if (!editable) return;
        setLoading(true);
        setErrors({});

        const body_html = editorRef.current?.flushAndGetData?.() ?? form.body_html;
        const payload = { ...form, body_html };

        const response = isEdit
            ? await updateContentPage(ID, payload)
            : await createContentPage(payload);

        if (response?.code === "success") {
            setNotification({
                open: true,
                msg: response?.warning || response?.message || "Saved.",
                type: response?.warning ? "warning" : "success",
            });
            setTimeout(() => navigate("/settings-content-pages"), 600);
        } else if (response?.code === "invalid_field") {
            setErrors(response?.errors || {});
            setNotification({ open: true, msg: "Please fix validation errors.", type: "error" });
        } else if (response?.code === "network_error") {
            setNotification({
                open: true,
                msg: response?.message || "Network response error, please try again later.",
                type: "error",
            });
        } else {
            setNotification({
                open: true,
                msg: response?.message || "Save failed.",
                type: "error",
            });
        }
        setLoading(false);
    };

    return (
        <div className="page-content">
            <LoadingDialog open={loading} />
            <Container fluid>
                <Notification notification={notification} setNotification={setNotification} />
                <Breadcrumb
                    title={isEdit ? "Edit Page" : "Add Page"}
                    backRoute="/settings-content-pages"
                    breadcrumbItems={[
                        { title: "Settings", link: "/settings" },
                        { title: "Category Pages", link: "/settings-content-pages" },
                        { title: isEdit ? "Edit" : "Add", link: "#" },
                    ]}
                />
                <Card>
                    <CardBody>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <CustomTextField
                                    title="Title"
                                    values={form.title}
                                    onChange={(v) => handleChange("title", v)}
                                    disabled={!editable}
                                    error={errors?.title?.[0]}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomTextField
                                    title="URL slug"
                                    values={form.slug}
                                    onChange={(v) => handleChange("slug", v)}
                                    disabled={!editable}
                                    placeholder="e.g. privacy-policy"
                                    error={errors?.slug?.[0]}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomTextField
                                    title="Meta description"
                                    values={form.meta_description}
                                    onChange={(v) => handleChange("meta_description", v)}
                                    disabled={!editable}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomTextField
                                    title="Meta keywords"
                                    values={form.meta_keywords}
                                    onChange={(v) => handleChange("meta_keywords", v)}
                                    disabled={!editable}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isToggleOn(form.status)}
                                            onChange={(e) => handleChange("status", e.target.checked ? 1 : 0)}
                                            disabled={!editable}
                                        />
                                    }
                                    label="Enabled"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isToggleOn(form.show_in_footer)}
                                            onChange={(e) => handleChange("show_in_footer", e.target.checked ? 1 : 0)}
                                            disabled={!editable}
                                        />
                                    }
                                    label="Show in footer"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isToggleOn(form.is_faq)}
                                            onChange={(e) => handleChange("is_faq", e.target.checked ? 1 : 0)}
                                            disabled={!editable}
                                        />
                                    }
                                    label="FAQ layout (accordion behaviour)"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Divider className="my-2" />
                                <FormEditors
                                    ref={editorRef}
                                    key={isEdit ? ID : "new"}
                                    title="Page content"
                                    value={form.body_html}
                                    setValue={(html) => handleChange("body_html", html)}
                                />
                            </Grid>
                        </Grid>
                        {editable && (
                            <div className="mt-4">
                                <CustomButton
                                    title="Save Changes"
                                    press={handleSave}
                                    backgroundColor="#101010"
                                    textColor="#FFFFFF"
                                    borderStyle="1px solid #101010"
                                />
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

const mapStateToProps = ({ Login }) => ({
    userPermission: Login.permissionArry,
});

export default connect(mapStateToProps)(EditContentPage);
